import { IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonContent, IonIcon, IonItem, IonLabel, IonList, IonListHeader, IonModal, IonSelect, IonSelectOption, IonText, IonTitle, IonToolbar, IonHeader } from "@ionic/react";
import { Assignment, StudentEnrollmentFromServer } from "../../../data-types/Types";
import Expandable from "./Expandable";
import TableActions from './TableActions';
import CreateAssignment from "./CreateAssignment";
import RosterManager from "./RosterManager";
import { useRoster } from "../services/RosterProvider";
import { useData } from "../services/DataProvider";
import { useState } from "react";
import AssignmentScheduleFields from "./AssignmentScheduleFields";
import { buildUtcMsFromLocalParts, formatAssignmentWindow, getLocalDateTimeParts } from "../utils/assignmentAvailability";
import { closeOutline, pencilOutline } from "ionicons/icons";

// Define explicit types for the summary object
type AssignmentSummary = {
  completed: number;
  quizName: string | undefined;
  inferredKnowledgeByStandard: {[studentID: string]: {[standardID: string]: number}};
  standardAverages?: {[standardID: string]: number};
};

type ClassInsightsProps = {
  classData: {
    displayName: string;
    shortCode: string;
    id?: string;
  };
  assignments: Assignment[];
  enrollments: StudentEnrollmentFromServer[];
  enrollmentCount?: number;
  assignmentsToSummaries: Record<string, AssignmentSummary>;
  standards: any[];
  isReadOnly?: boolean;
};

const ClassInsights: React.FC<ClassInsightsProps> = ({
  classData,
  assignments,
  enrollments,
  enrollmentCount,
  assignmentsToSummaries,
  standards,
  isReadOnly = false
}) => {
  const { getName } = useRoster();
  const { createOwnedRecord, synching } = useData();
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [selectedPriorByAssignment, setSelectedPriorByAssignment] = useState<Record<string, string>>({});
  const [scheduleFields, setScheduleFields] = useState({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
  });
  const [scheduleError, setScheduleError] = useState("");
  const displayNameFor = (studentId?: string) => {
    if (!studentId) {
      return "";
    }
    return getName(classData.shortCode, studentId) || studentId;
  };
  const link = (window.location.protocol + "//") + window.location.hostname.toLowerCase() + (window.location.port ? ":" + window.location.port : "") + "/class/" + classData.shortCode;
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const openScheduleEditor = (assignment: Assignment) => {
    const startParts = getLocalDateTimeParts(assignment.startAtUtcMs, assignment.startAtHasTime, "start");
    const endParts = getLocalDateTimeParts(assignment.endAtUtcMs, assignment.endAtHasTime, "end");
    setScheduleFields({
      startDate: startParts.date,
      startTime: startParts.time,
      endDate: endParts.date,
      endTime: endParts.time,
    });
    setScheduleError("");
    setEditingAssignment(assignment);
  };

  const handleScheduleSave = async () => {
    if (!editingAssignment?.id) {
      setScheduleError("Missing assignment id.");
      return;
    }
    const startAt = buildUtcMsFromLocalParts(scheduleFields.startDate, scheduleFields.startTime, "start");
    const endAt = buildUtcMsFromLocalParts(scheduleFields.endDate, scheduleFields.endTime, "end");

    if (startAt.utcMs && endAt.utcMs && startAt.utcMs > endAt.utcMs) {
      setScheduleError("Start date/time must be before the end date/time.");
      return;
    }

    const updated: Assignment = { ...editingAssignment };

    if (startAt.utcMs !== undefined) {
      updated.startAtUtcMs = startAt.utcMs;
      updated.startAtHasTime = startAt.hasTime;
    } else {
      delete updated.startAtUtcMs;
      delete updated.startAtHasTime;
    }

    if (endAt.utcMs !== undefined) {
      updated.endAtUtcMs = endAt.utcMs;
      updated.endAtHasTime = endAt.hasTime;
    } else {
      delete updated.endAtUtcMs;
      delete updated.endAtHasTime;
    }

    try {
      await createOwnedRecord("finlitri-assignments", updated);
      setEditingAssignment(null);
    } catch (error: any) {
      setScheduleError(error?.message || "Failed to update schedule.");
    }
  };

  const standardsById = new Map(standards.map((standard: any) => [standard.id, standard]));
  const gradeList = Array.from(
    new Set(
      standards
        .map((standard: any) => standard?.grade)
        .filter((grade: string | undefined): grade is string => !!grade)
    )
  ).sort((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10));

  // NOTE: Ordering could be made more robust using per-student response timestamps,
  // but that would be more expensive than using assignment start/end fields.
  const getAssignmentStart = (assignment: Assignment) => (
    assignment.startAtUtcMs ?? Number.NEGATIVE_INFINITY
  );

  const getAssignmentEnd = (assignment: Assignment) => (
    assignment.endAtUtcMs ?? Number.POSITIVE_INFINITY
  );

  const compareAssignmentsByDate = (a: Assignment, b: Assignment) => {
    const startDiff = getAssignmentStart(a) - getAssignmentStart(b);
    if (startDiff !== 0) {
      return startDiff;
    }
    return getAssignmentEnd(a) - getAssignmentEnd(b);
  };

  const buildStudentGradeAverages = (summary: AssignmentSummary | undefined) => {
    const inferred = summary?.inferredKnowledgeByStandard || {};
    const results: Record<string, Record<string, number | undefined>> = {};
    Object.entries(inferred).forEach(([studentId, standardScores]) => {
      const gradeScores: Record<string, number | undefined> = {};
      gradeList.forEach((grade) => {
        const values = Object.entries(standardScores)
          .filter(([standardId]) => standardsById.get(standardId)?.grade === grade)
          .map(([, value]) => value);
        if (values.length === 0) {
          gradeScores[grade] = undefined;
          return;
        }
        const average = values.reduce((sum, value) => sum + value, 0) / values.length;
        gradeScores[grade] = average;
      });
      results[studentId] = gradeScores;
    });
    return results;
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined) {
      return "-";
    }
    return `${(value * 100).toFixed(1)}%`;
  };

  const buildGradeStrandRows = (summary: AssignmentSummary | undefined) => {
    const inferred = summary?.inferredKnowledgeByStandard || {};
    const standardAverages = summary?.standardAverages || {};
    const standardTotals = new Map<string, { total: number; count: number; grade: string; strand: string }>();
    if (Object.keys(standardAverages).length > 0) {
      Object.entries(standardAverages).forEach(([standardId, score]) => {
        const standard = standardsById.get(standardId);
        if (!standard) {
          return;
        }
        const grade = standard.grade || "Unknown";
        const strand = standard.topic || standard.subject || "Unknown";
        const key = `${grade}::${strand}`;
        const current = standardTotals.get(key) || { total: 0, count: 0, grade, strand };
        current.total += score;
        current.count += 1;
        standardTotals.set(key, current);
      });
    } else {
      Object.entries(inferred).forEach(([studentId, standardScores]) => {
        Object.entries(standardScores).forEach(([standardId, score]) => {
          const standard = standardsById.get(standardId);
          if (!standard) {
            return;
          }
          const grade = standard.grade || "Unknown";
          const strand = standard.topic || standard.subject || "Unknown";
          const key = `${grade}::${strand}`;
          const current = standardTotals.get(key) || { total: 0, count: 0, grade, strand };
          current.total += score;
          current.count += 1;
          standardTotals.set(key, current);
        });
      });
    }
    return Array.from(standardTotals.values())
      .map((entry) => {
        const average = entry.count > 0 ? entry.total / entry.count : 0;
        return [entry.grade, entry.strand, `${(average * 100).toFixed(1)}%`];
      })
      .sort((a, b) => {
        if (a[0] === b[0]) {
          return String(a[1]).localeCompare(String(b[1]));
        }
        return Number.parseInt(String(a[0]), 10) - Number.parseInt(String(b[0]), 10);
      });
  };

  return <>
    <IonCard>
      <IonCardHeader><IonCardTitle>Class Overview and Students</IonCardTitle></IonCardHeader>
      <IonCardContent>
        <p>Link for students: <a href={link}>{link}</a></p>
        <h2>Enrolled Students: {enrollmentCount ?? enrollments.length}</h2>
        {!isReadOnly && (
          <Expandable>
            <RosterManager classCode={classData.shortCode} enrollments={enrollments} />
          </Expandable>
        )}
      </IonCardContent>
    </IonCard>

    {!isReadOnly && isLocalhost && (
      <IonCard>
        <IonCardHeader><IonCardTitle>Create New Assignment</IonCardTitle></IonCardHeader>
        <IonCardContent>
          <Expandable>
            <CreateAssignment classID={classData.shortCode} />
          </Expandable>
        </IonCardContent>
      </IonCard>
    )}

    {assignments.map((assignment, index) => {
      const assignmentKey = assignment.id || `assignment-${index}`;
      const summary = assignmentsToSummaries[assignment.id || ""];
      const scheduleSummary = formatAssignmentWindow(assignment);
      const hasCompleted = (summary?.completed ?? 0) > 0;
      const hasStudentMastery = Object.keys(summary?.inferredKnowledgeByStandard || {}).length > 0;
      const priorAssignments = assignments
        .filter((candidate) => candidate !== assignment)
        .filter((candidate) => compareAssignmentsByDate(candidate, assignment) < 0)
        .sort(compareAssignmentsByDate);
      const defaultPriorId = priorAssignments[0]?.id;
      const selectedPriorId = selectedPriorByAssignment[assignmentKey] ?? defaultPriorId;
      const selectedPriorSummary = selectedPriorId ? assignmentsToSummaries[selectedPriorId] : undefined;
      const currentAverages = buildStudentGradeAverages(summary);
      const priorAverages = buildStudentGradeAverages(selectedPriorSummary);
      const studentEntries = Object.keys(currentAverages)
        .map((studentId) => ({
          studentId,
          displayName: displayNameFor(studentId),
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
      const exportRows = studentEntries.map((entry) => [
        entry.displayName,
        ...gradeList.map((grade) => formatPercent(currentAverages[entry.studentId]?.[grade])),
      ]);
      // TODO: Scope mastery comparisons to the assessed standards for each assignment.
      const classAverageRow = gradeList.map((grade) => {
        const currentValues = studentEntries
          .map((entry) => currentAverages[entry.studentId]?.[grade])
          .filter((value): value is number => value !== undefined);
        const growthValues = studentEntries
          .map((entry) => {
            const current = currentAverages[entry.studentId]?.[grade];
            const previous = priorAverages[entry.studentId]?.[grade];
            if (current === undefined || previous === undefined) {
              return undefined;
            }
            return current - previous;
          })
          .filter((value): value is number => value !== undefined);
        const currentAverage = currentValues.length > 0
          ? currentValues.reduce((sum, value) => sum + value, 0) / currentValues.length
          : undefined;
        const growthAverage = growthValues.length > 0
          ? growthValues.reduce((sum, value) => sum + value, 0) / growthValues.length
          : undefined;
        return { currentAverage, growthAverage };
      });

      return <IonCard key={assignment.id || index}>
        <IonCardHeader>
          <IonCardTitle>{assignment.title || ("Assigned Quiz: " + (summary?.quizName || assignment.quizID))}</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {assignment.description ? <p><b>Description: </b>{assignment.description}</p> : <></>}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em", flexWrap: "wrap" }}>
            <p style={{ margin: 0 }}><b>Availability:</b> {scheduleSummary}</p>
            {!isReadOnly && (
              <IonButton
                size="small"
                fill="outline"
                onClick={() => openScheduleEditor(assignment)}
                disabled={synching}
                aria-label="Edit assignment availability"
              >
                <IonIcon icon={pencilOutline} slot="start" />
                Edit
              </IonButton>
            )}
          </div>
          {isReadOnly ? (
            <p>Responses so far: <b>{summary?.completed ?? 0}</b>.</p>
          ) : (
            <p>Completed by <b>{summary?.completed}</b> of <b>{enrollments.length}</b>.</p>
          )}

          {hasCompleted ? (
            <>
              <br />

              {hasStudentMastery && (
                <>
                  <h2>Student Mastery by Grade</h2>
                  <Expandable>
                    {priorAssignments.length > 0 && (
                      <IonItem lines="none">
                        <IonLabel>Compare to previous assignment</IonLabel>
                        <IonSelect
                          value={selectedPriorId}
                          placeholder="Select assignment"
                          onIonChange={(event) => {
                            const nextId = event.detail.value;
                            if (!nextId) {
                              return;
                            }
                            setSelectedPriorByAssignment((prev) => ({
                              ...prev,
                              [assignmentKey]: nextId,
                            }));
                          }}
                        >
                          {priorAssignments.map((priorAssignment) => {
                            const label = priorAssignment.title
                              || assignmentsToSummaries[priorAssignment.id || ""]?.quizName
                              || priorAssignment.quizID
                              || "Assignment";
                            const subtitle = formatAssignmentWindow(priorAssignment);
                            return (
                              <IonSelectOption key={priorAssignment.id} value={priorAssignment.id}>
                                {label} ({subtitle})
                              </IonSelectOption>
                            );
                          })}
                        </IonSelect>
                      </IonItem>
                    )}
                    <IonText color="medium">
                      <p style={{ marginTop: 0 }}>(Minor accuracy updates may slightly adjust estimates over time.)</p>
                    </IonText>
                    <TableActions 
                      headers={["Student", ...gradeList.map((grade) => `Grade ${grade}`)]}
                      rows={exportRows}
                      filename={`${assignment.title || summary?.quizName || 'assignment'}_student_grade_mastery`}
                    />
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Student</th>
                            {gradeList.map((grade) => (
                              <th key={grade} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                Grade {grade}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr key="class-average" style={{ fontWeight: 600 }}>
                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>Class Average</td>
                            {classAverageRow.map((entry, gradeIndex) => {
                              const growthText = entry.growthAverage !== undefined
                                ? `${entry.growthAverage >= 0 ? "+" : ""}${(entry.growthAverage * 100).toFixed(1)}%`
                                : null;
                              const growthColor = entry.growthAverage === undefined
                                ? undefined
                                : entry.growthAverage > 0
                                  ? "var(--ion-color-success)"
                                  : entry.growthAverage < 0
                                    ? "var(--ion-color-danger)"
                                    : "var(--ion-color-medium)";
                              return (
                                <td key={`class-average-${gradeIndex}`} style={{ border: '1px solid #ddd', padding: '8px' }}>
                                  <span>{formatPercent(entry.currentAverage)}</span>
                                  {growthText && (
                                    <span style={{ marginLeft: "0.35rem", color: growthColor, fontSize: "0.85em" }}>
                                      ({growthText})
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          {studentEntries.map((entry) => (
                            <tr key={entry.studentId}>
                              <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                                {entry.displayName}
                              </td>
                              {gradeList.map((grade, gradeIndex) => {
                                const currentValue = currentAverages[entry.studentId]?.[grade];
                                const previousValue = priorAverages[entry.studentId]?.[grade];
                                const hasGrowth = currentValue !== undefined && previousValue !== undefined;
                                let growthText: string | null = null;
                                let growthColor: string | undefined;
                                if (hasGrowth) {
                                  const growthValue = currentValue - previousValue;
                                  growthText = `${growthValue >= 0 ? "+" : ""}${(growthValue * 100).toFixed(1)}%`;
                                  growthColor = growthValue > 0
                                    ? "var(--ion-color-success)"
                                    : growthValue < 0
                                      ? "var(--ion-color-danger)"
                                      : "var(--ion-color-medium)";
                                }
                                return (
                                  <td key={`${entry.studentId}-${gradeIndex}`} style={{ border: '1px solid #ddd', padding: '8px' }}>
                                    <span>{formatPercent(currentValue)}</span>
                                    {growthText && (
                                      <span style={{ marginLeft: "0.35rem", color: growthColor, fontSize: "0.85em" }}>
                                        ({growthText})
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Expandable>

                  <div style={{ marginTop: "1.25rem" }} />
                </>
              )}
              <h2>Class Mastery by Grade and Strand</h2>
              <Expandable>
                <IonText color="medium">
                  <p style={{ marginTop: 0 }}>(Minor accuracy updates may slightly adjust estimates over time.)</p>
                </IonText>
                <TableActions 
                  headers={["Grade", "Strand", "Average Mastery"]}
                  rows={buildGradeStrandRows(summary)}
                  filename={`${assignment.title || summary?.quizName || 'assignment'}_grade_strand_mastery`}
                />
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Grade</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Strand</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Average Mastery</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buildGradeStrandRows(summary).map((row) => (
                        <tr key={`${row[0]}-${row[1]}`}>
                          {row.map((cell, index) => (
                            <td key={`${row[0]}-${row[1]}-${index}`} style={{ border: '1px solid #ddd', padding: '8px' }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Expandable>
            </>
          ) : null}

        </IonCardContent>
      </IonCard>
    })}
    <IonModal
      isOpen={!!editingAssignment}
      onDidDismiss={() => setEditingAssignment(null)}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Edit assignment availability</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => setEditingAssignment(null)}
              aria-label="Close"
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {editingAssignment?.title && <p><b>{editingAssignment.title}</b></p>}
        <AssignmentScheduleFields
          {...scheduleFields}
          onChange={setScheduleFields}
        />
        {scheduleError && <IonText color="danger"><p>{scheduleError}</p></IonText>}
        <div style={{ display: "flex", gap: "0.5em", marginTop: "1em" }}>
          <IonButton onClick={() => setEditingAssignment(null)} disabled={synching}>
            Cancel
          </IonButton>
          <IonButton onClick={handleScheduleSave} disabled={synching}>
            Save
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  </>
}

export default ClassInsights; 